import type { ConnectGrantType, ConnectMemberRole } from "@ando/core";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  buttonVariants,
  cn,
  toast,
} from "@ando/ui";
import type { I18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  Check,
  Copy,
  Database,
  ExternalLink,
  FileText,
  Loader2,
  type LucideIcon,
  Plug,
  X,
} from "lucide-react";
import * as React from "react";

/**
 * connectShared — small DRY atoms reused across the Connect management forms
 * (``GrantManager`` / ``MemberManager`` / ``ChatbotPanel`` / ``ActivityFeed``).
 *
 * Nothing here fetches or mutates; these are pure presentation + a thin
 * confirm-dialog wrapper over the shared ``@ando/ui`` ``AlertDialog`` so every
 * destructive Connect action (revoke grant / revoke member / disable chatbot)
 * gets the SAME accidental-destruction guard (Esc-blocked, focus-on-cancel)
 * instead of each form re-deriving its own modal chrome.
 *
 * The UNDO half of the destructive pattern lives at the call-site via
 * ``toast(..., { action: { label, onClick } })`` (sonner) — the mutation hooks
 * already re-add the row on a failed/undone re-grant, so undo just replays the
 * authoring mutation.
 */

// ---------------------------------------------------------------------------
// Grant type → icon. ONE source of truth shared by the grant-row renderer,
// the type picker, and SpaceScopeBanner's scope chips.
// ---------------------------------------------------------------------------

export const GRANT_TYPE_ICON: Record<ConnectGrantType, LucideIcon> = {
  knowledge: Database,
  connector: Plug,
  document: FileText,
};

// ---------------------------------------------------------------------------
// Role → localized label. ONE source of truth so the member row, the shared
// space card, and the role picker never re-spell "viewer"/"editor" — and the
// raw enum NEVER leaks to the UI (no ``capitalize`` of an English token).
// ---------------------------------------------------------------------------

export function roleLabel(role: ConnectMemberRole | string | null | undefined, i18n: I18n): string {
  switch (role) {
    case "editor":
      return i18n._(msg`Editor`);
    case "viewer":
      return i18n._(msg`Visualizzatore`);
    default:
      return i18n._(msg`Ospite`);
  }
}

// ---------------------------------------------------------------------------
// Relative time — "fra 3 giorni" / "2 ore fa". Intl-driven (locale-aware via
// the active Lingui locale), no extra dep. Returns null for empty input so
// callers can omit the chip entirely.
// ---------------------------------------------------------------------------

const REL_UNITS: ReadonlyArray<readonly [Intl.RelativeTimeFormatUnit, number]> = [
  ["year", 1000 * 60 * 60 * 24 * 365],
  ["month", 1000 * 60 * 60 * 24 * 30],
  ["day", 1000 * 60 * 60 * 24],
  ["hour", 1000 * 60 * 60],
  ["minute", 1000 * 60],
];

export function formatRelative(iso: string | null | undefined, locale: string): string | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const diff = then - Date.now();
  const abs = Math.abs(diff);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  for (const [unit, ms] of REL_UNITS) {
    if (abs >= ms || unit === "minute") {
      return rtf.format(Math.round(diff / ms), unit);
    }
  }
  return rtf.format(0, "minute");
}

export function formatAbsolute(iso: string | null | undefined, locale: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

// ---------------------------------------------------------------------------
// ConfirmDialog — DRY destructive guard. Esc / outside-click blocked, focus
// pinned on Cancel; the confirm button carries the destructive variant so the
// commitment is spatially + semantically separated from the cancel.
// ---------------------------------------------------------------------------

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: React.ReactNode;
  readonly description: React.ReactNode;
  readonly confirmLabel: React.ReactNode;
  readonly cancelLabel: React.ReactNode;
  readonly onConfirm: () => void;
  readonly pending?: boolean;
  /** ``destructive`` (default) tints the confirm button rose. */
  readonly tone?: "destructive" | "primary";
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  onConfirm,
  pending = false,
  tone = "destructive",
}: ConfirmDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-[420px]">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[15px]">{title}</AlertDialogTitle>
          <AlertDialogDescription className="text-[12.5px] text-ink-2">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            className={cn(buttonVariants({ variant: "ghost", size: "md" }), "mt-0")}
            disabled={pending}
          >
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            className={cn(buttonVariants({ variant: tone, size: "md" }), "gap-1.5")}
            disabled={pending}
            onClick={(e) => {
              // Keep the dialog mounted while the mutation runs so the spinner
              // is visible; the caller closes it on settle.
              e.preventDefault();
              onConfirm();
            }}
          >
            {pending ? <Loader2 className="animate-spin" size={14} aria-hidden /> : null}
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ---------------------------------------------------------------------------
// Section primitives — a labelled block + a "+ add" affordance header, reused
// by every tab so the rail rhythm (8px) stays uniform.
// ---------------------------------------------------------------------------

/**
 * RailEmptyInline — the centred empty-state used across the rail tabs. Mirrors
 * the route-local ``RailEmpty`` so the management forms share ONE empty look.
 */
export function RailEmptyInline({
  icon,
  title,
  body,
}: {
  readonly icon: React.ReactNode;
  readonly title: string;
  readonly body: string;
}) {
  return (
    <div className="m-auto flex max-w-[18rem] flex-col items-center gap-2 px-3 py-10 text-center">
      <span
        aria-hidden
        className="flex h-9 w-9 items-center justify-center rounded-xl bg-cream-deep text-ink-3"
      >
        {icon}
      </span>
      <p className="font-semibold text-[12.5px] text-ink-1">{title}</p>
      <p className="text-[11.5px] text-ink-2">{body}</p>
    </div>
  );
}

export function RailSectionHeader({
  title,
  action,
}: {
  readonly title: React.ReactNode;
  readonly action?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-2 px-0.5 pt-1 pb-1.5">
      <span className="font-semibold text-[10px] text-ink-3 uppercase tracking-[0.1em]">
        {title}
      </span>
      {action}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CopyableField — a labelled read-only value with a Copy button (+ optional
// Open). ONE implementation shared by the chatbot public-link/embed panel and
// the member invite-link block, so the copy affordance (transient "Copiato"
// confirmation, focus rings, mono multiline) stays identical everywhere.
// ---------------------------------------------------------------------------

const COPY_BTN_CLASS = cn(
  "inline-flex items-center gap-1 rounded-md bg-ink-2 px-2.5 py-1.5 text-[11px] text-white hover:brightness-110",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/55 focus-visible:ring-offset-1",
);
const OPEN_BTN_CLASS = cn(
  "inline-flex items-center justify-center gap-1 rounded-md border border-black/10 bg-white px-2.5 py-1.5 text-[11px] text-ink-1 hover:bg-white/85",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/55 focus-visible:ring-offset-1",
);

export function CopyableField({
  icon,
  label,
  value,
  multiline = false,
  openHref,
  fieldAriaLabel,
}: {
  readonly icon: React.ReactNode;
  readonly label: React.ReactNode;
  readonly value: string;
  readonly multiline?: boolean;
  readonly openHref?: string;
  /** aria-label for the read-only input/textarea (falls back to ``label``). */
  readonly fieldAriaLabel?: string;
}) {
  const { i18n } = useLingui();
  const [copied, setCopied] = React.useState(false);
  const ariaLabel = fieldAriaLabel ?? (typeof label === "string" ? label : i18n._(msg`Valore`));
  const handleCopy = () => {
    if (typeof navigator === "undefined" || !navigator.clipboard) return;
    void navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1200);
      })
      // Clipboard write can reject (permission denied / insecure context):
      // surface a toast instead of leaving the user with a silently dead button.
      .catch(() => toast.error(i18n._(msg`Copia non riuscita. Copia manualmente.`)));
  };
  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-hairline/70 bg-white/55 px-3 py-2.5">
      <span className="flex items-center gap-1.5 font-medium text-[11.5px] text-ink-1">
        {icon}
        {label}
      </span>
      <div className="flex items-start gap-2">
        {multiline ? (
          <textarea
            readOnly
            value={value}
            rows={2}
            aria-label={ariaLabel}
            className="min-w-0 flex-1 resize-none rounded border border-black/10 bg-white px-2 py-1.5 font-mono text-[10.5px] text-ink-1"
          />
        ) : (
          <input
            readOnly
            value={value}
            aria-label={ariaLabel}
            className="min-w-0 flex-1 rounded border border-black/10 bg-white px-2 py-1.5 text-[11px] text-ink-1"
          />
        )}
        <div className="flex shrink-0 flex-col gap-1.5">
          <button type="button" onClick={handleCopy} className={COPY_BTN_CLASS}>
            {copied ? (
              <>
                <Check size={12} strokeWidth={2} aria-hidden /> <Trans>Copiato</Trans>
              </>
            ) : (
              <>
                <Copy size={12} strokeWidth={2} aria-hidden /> <Trans>Copia</Trans>
              </>
            )}
          </button>
          {openHref ? (
            <a href={openHref} target="_blank" rel="noopener noreferrer" className={OPEN_BTN_CLASS}>
              <ExternalLink size={12} strokeWidth={2} aria-hidden />
              <Trans>Apri</Trans>
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TokenInput — a tiny chip/token input (DRY across Connect forms). The user
// types a value + Enter (or comma) → it becomes a removable chip; Backspace on
// an empty field pops the last chip; the ✕ removes a specific one. The parent
// owns the committed ``value`` array (already trimmed + de-duped + non-empty);
// this never mutates raw text. Replaces the "plain input split on commas"
// anti-pattern wherever a free-form list is authored.
// ---------------------------------------------------------------------------

export interface TokenInputProps {
  /** Committed tokens (source of truth lives in the parent). */
  readonly value: readonly string[];
  /** Called with the next token set (trimmed, de-duped, non-empty). */
  readonly onChange: (next: readonly string[]) => void;
  readonly id?: string;
  readonly placeholder?: string;
  /** aria-label for the text field (the chips carry their own remove labels). */
  readonly ariaLabel?: string;
  /** Localized "remove {token}" label builder for each chip's ✕. */
  readonly removeLabel?: (token: string) => string;
}

export function TokenInput({
  value,
  onChange,
  id,
  placeholder,
  ariaLabel,
  removeLabel,
}: TokenInputProps) {
  const { i18n } = useLingui();
  const [draft, setDraft] = React.useState("");
  const inputRef = React.useRef<HTMLInputElement>(null);

  const commit = React.useCallback(
    (raw: string) => {
      const token = raw.trim();
      if (!token) return;
      if (value.includes(token)) {
        setDraft("");
        return;
      }
      onChange([...value, token]);
      setDraft("");
    },
    [value, onChange],
  );

  const removeAt = React.useCallback(
    (token: string) => {
      onChange(value.filter((t) => t !== token));
    },
    [value, onChange],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit(draft);
    } else if (e.key === "Backspace" && draft === "" && value.length > 0) {
      e.preventDefault();
      removeAt(value[value.length - 1]!);
    }
  };

  return (
    // A native ``<label>`` wrapper: clicking the chip gutter focuses the inner
    // input for free (no JS click/keydown handler, no contradictory
    // ``role="presentation"``). ALL keyboard handling lives on the ``<input>``
    // below (Enter/comma commit, Backspace pops the last chip). Clicking a
    // chip's remove button is its own interactive target, so label-focus does
    // not steal that click.
    <label
      className={cn(
        "flex flex-wrap items-center gap-1.5 rounded-md border border-black/10 bg-white px-2 py-1.5",
        "focus-within:ring-2 focus-within:ring-ando-orange/40",
      )}
    >
      {value.map((token) => (
        <span
          key={token}
          className="inline-flex items-center gap-1 rounded border border-hairline bg-cream-deep/70 py-0.5 pr-1 pl-1.5 text-[11.5px] text-ink-1"
        >
          <span className="max-w-[10rem] truncate">{token}</span>
          <button
            type="button"
            onClick={() => removeAt(token)}
            aria-label={(removeLabel ?? ((t) => i18n._(msg`Rimuovi ${t}`)))(token)}
            className={cn(
              "flex h-4 w-4 shrink-0 items-center justify-center rounded text-ink-3 transition-colors",
              "hover:bg-rose-50 hover:text-rose-600",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400/45",
            )}
          >
            <X size={11} strokeWidth={2.4} aria-hidden />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        id={id}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => commit(draft)}
        placeholder={value.length === 0 ? placeholder : undefined}
        aria-label={ariaLabel}
        className="min-w-[6rem] flex-1 bg-transparent text-[12px] text-ink-1 outline-none placeholder:text-ink-3"
      />
    </label>
  );
}
